import * as React from "react";
import * as _ from "lodash";
import classNames from "classnames";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { DomAttribute } from "@bizform/type/model/dom/DomAttribute.model";
import { ComponentHelper, Debug } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../../bf_component/AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


interface GridLayoutColumn {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
}

export interface GridLayoutRow {
    rows: number;
    cols: {
        count: number;
        min: number;
        max: number;
        items: Array<GridLayoutColumn>;
    };
}

type Props = {
};

type WrappedCompState = Props & {
    // attribute
    model: GridLayoutRow;
    gridClass?: string;
    rowClass?: string;
    cellClass?: string;
    gridPaddingTop?: string;
    gridPaddingRight?: string;
    gridPaddingBottom?: string;
    gridPaddingLeft?: string;
    rowMarginTop?: string;
    rowMarginRight?: string;
    rowMarginBottom?: string;
    rowMarginLeft?: string;
    cellPaddingTop?: string;
    cellPaddingRight?: string;
    cellPaddingBottom?: string;
    cellPaddingLeft?: string;
    rowHeight?: string;
    rowOverflow?: string;
    // event
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
};

/**
 * GridLayout component element
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfGridLayout extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_ity2zodk4a9TVn7U05QMfIK75H6v9uV",
        category: ComponentCategory.PANEL,
        label: I18n.layout.gridLayout.label,
        description: I18n.layout.gridLayout.description,
        icon: "",
        property: null
    };

    focus(): void {
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, null>(
                property,
                this
            );
            this.setState(newState);
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    emitFormState(value: string): void {
    }

    emitHasContainerKey(): void {
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfGridLayout)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    static defaultProps = {
    };

    private _ref: object;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                model: null
            }
        };

        this._setRef = this._setRef.bind(this);
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                case ComponentElementDisplayMode.READONLY:
                case ComponentElementDisplayMode.DISABLED:
                case ComponentElementDisplayMode.PRINT:
                    return this._render4ReadOnly();
                default:
                    return null;
            }
        }
    }

    private _render4ReadOnly(): React.ReactNode {
        const _this = this;
        const model = _.get(this.state.wrappedCompState, "model", []);
        const rowCount = model && "rows" in model ? model.rows : 1;
        const rows = _.fill(new Array<string>(rowCount), "");
        const rowContent = rows.map(function (value, index) {
            return _this._createRow(index);
        });
        const { gridClass, gridPaddingTop, gridPaddingRight, gridPaddingBottom, gridPaddingLeft, rowHeight, rowOverflow } = this.state.wrappedCompState;
        const padding: any = {};
        if (gridPaddingTop) {
            padding.paddingTop = gridPaddingTop;
        }
        if (gridPaddingRight) {
            padding.paddingRight = gridPaddingRight;
        }
        if (gridPaddingBottom) {
            padding.paddingBottom = gridPaddingBottom;
        }
        if (gridPaddingLeft) {
            padding.paddingLeft = gridPaddingLeft;
        }
        let className = "container-fluid";
        if (gridClass) {
            className += " " + gridClass;
        }
        const height = rowHeight && _.endsWith(_.trim(rowHeight), "%") ? {height: "100%"} : {};
        const overflow = rowOverflow ? {overflow: rowOverflow} : {};

        return (
            <div ref={this._setRef} className={className} style={{...padding, ...height, ...overflow}}>
                {rowContent}
            </div>
        );
    }

    private _createRow(rowIndex: number): React.ReactNode {
        const _this = this;
        const cols = _.get(this.state, "wrappedCompState.model.cols.items", []);
        const { rowClass, rowMarginTop, rowMarginRight, rowMarginBottom, rowMarginLeft, rowHeight, rowOverflow } = this.state.wrappedCompState;
        const margin: any = {};
        if (rowMarginTop) {
            margin.marginTop = rowMarginTop;
        }
        if (rowMarginRight) {
            margin.marginRight = rowMarginRight;
        }
        if (rowMarginBottom) {
            margin.marginBottom = rowMarginBottom;
        }
        if (rowMarginLeft) {
            margin.marginLeft = rowMarginLeft;
        }
        let className = "row";
        if (rowClass) {
            className += " " + rowClass;
        }
        const columnContent = cols.map(function (col, index) {
            return _this._createColumn(col, rowIndex, index);
        });
        const height = {
            height: rowHeight
        };
        const overflow = {
            overflow: rowOverflow
        };

        return (
            <div key={rowIndex} className={className} style={{...margin, ...height, ...overflow}}>
                {columnContent}
            </div>
        );
    }

    private _createColumn(col: GridLayoutColumn, rowIndex: number, colIndex: number): React.ReactNode {
        const bfContainer = {[DomAttribute.CONTAINER.toString()]: `${rowIndex}_${colIndex}`};
        const { cellClass, cellPaddingTop, cellPaddingRight, cellPaddingBottom, cellPaddingLeft } = this.state.wrappedCompState;
        const padding: any = {};
        if (cellPaddingTop) {
            padding.paddingTop = cellPaddingTop;
        }
        if (cellPaddingRight) {
            padding.paddingRight = cellPaddingRight;
        }
        if (cellPaddingBottom) {
            padding.paddingBottom = cellPaddingBottom;
        }
        if (cellPaddingLeft) {
            padding.paddingLeft = cellPaddingLeft;
        }
        const className = classNames(
            "col",
            {["col-" + col.xs]: col.xs},
            {["col-sm-" + col.sm]: col.sm},
            {["col-md-" + col.md]: col.md},
            {["col-lg-" + col.lg]: col.lg},
            {["col-xl-" + col.xl]: col.xl},
            cellClass
        );
        const height = {
            height: "100%"
        };

        const overflow = {
        };

        return (
            <div key={colIndex} className={className} {...bfContainer} style={{...padding, ...height, ...overflow}}>
            </div>
        );
    }

    private _setRef(ref): void {
        if (ref) {
            this._ref = ref;
            this.readyComponent();
        }
    }
}
